import { useCallback, useState } from "react";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../Containers/PopupModalContainer";
import { AccountData, PlanTypeV4 } from "@markit/common.types";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import {
  filterUndefinedValues,
  hasSubscription,
  hasSubscriptionUnlockedAdvancedData,
  isBusinessSubscription,
  isProSubscription,
} from "@markit/common.utils";
import ProfilePic from "../ProfilePic";
import { getAccountState } from "../../redux/slices/accountSlice";
import { useSelector } from "react-redux";
import { getUserData } from "../../utils/FirebaseUtils";
import { CircularProgress } from "@mui/material";
import { Colors } from "../../utils/colors";
import { getUserTotalMessagesAllTime } from "../../utils/subscriptionUtils";

interface ReferralHistoryModalProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
}

const ReferralHistoryModal = (props: ReferralHistoryModalProps) => {
  const { isVisible, setIsVisible } = props;
  const { accountData } = useSelector(getAccountState).account;
  const [refereeUsers, setRefereeUsers] = useState<AccountData[]>([]);
  const [refereeNumMessages, setRefereeNumMessages] = useState<
    Record<string, number>
  >({});
  const [loading, setLoading] = useState(true);

  useAsyncOnMount(async () => {
    const foundUsers = await Promise.all(
      accountData.profileReferrals.map(async (userId) => {
        const userData = await getUserData(userId);
        if (userData) {
          const totalMessages = await getUserTotalMessagesAllTime(userData);
          setRefereeNumMessages((prev) => ({
            ...prev,
            [userId]: totalMessages,
          }));
        }
        return userData;
      })
    );
    const definedUsers = filterUndefinedValues(foundUsers);
    setRefereeUsers(definedUsers);

    setLoading(false);
  });

  const renderItem = useCallback(
    (user: AccountData) => {
      const totalMessages = refereeNumMessages[user.uid] || 0;
      const isBusinessSub = isBusinessSubscription(user);
      const isProSub = isProSubscription(user);
      const subscriptionPlan = isBusinessSub
        ? PlanTypeV4.BUSINESS
        : isProSub
          ? PlanTypeV4.PRO
          : PlanTypeV4.ESSENTIAL;
      const successfulReward = totalMessages >= subscriptionPlan.baselineLimit;
      return (
        <div className="AlignedRow" key={user.uid} style={{ gap: 14 }}>
          <ProfilePic user={user} size={55} />
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <span className="bodyMedium">{user.fullName}</span>
            <span className="smallBodySubtext">
              {hasSubscription(user) ? subscriptionPlan.name : "No"} Plan
              Referral
            </span>
            {hasSubscriptionUnlockedAdvancedData(user) ? (
              <span
                className="smallBodySubtext"
                style={{
                  color: successfulReward ? Colors.GREEN2 : Colors.ORANGE1,
                }}
              >
                {successfulReward ? "Successful" : "Pending"}{" "}
                {isBusinessSub ? "$300" : "$60"} Reward
              </span>
            ) : (
              <span className="smallBodySubtext" style={{ color: Colors.RED3 }}>
                Ineligible for Reward
              </span>
            )}
            {hasSubscriptionUnlockedAdvancedData(user) ? (
              <span
                className="smallBodySubtext"
                style={{
                  color: successfulReward ? Colors.GREEN2 : Colors.ORANGE1,
                }}
              >
                {successfulReward
                  ? `>${
                      isBusinessSubscription(user) ? "10,000" : "2,500"
                    } credits used`
                  : `${totalMessages.toLocaleString()}/${
                      isBusinessSubscription(user) ? "10,000" : "2,500"
                    } credits used`}
              </span>
            ) : (
              <span className="smallBodySubtext" style={{ color: Colors.RED3 }}>
                {hasSubscription(user)
                  ? "User must upgrade for valid referral plan"
                  : "Free plan, must upgrade to Pro or Business"}
              </span>
            )}
          </div>
        </div>
      );
    },
    [refereeNumMessages]
  );

  return (
    <PopupModalContainer
      headerComp={
        <div className="ColumnNormal" style={{ gap: 20 }}>
          <span className="sectionTitle">
            Referral History ({accountData.profileReferrals.length})
          </span>
        </div>
      }
      valueComp={
        loading ? (
          <div className="ColumnCentering">
            <CircularProgress style={{ color: Colors.GRAY1 }} size={30} />
          </div>
        ) : (
          <div className="ColumnNormal" style={{ gap: 14 }}>
            {refereeUsers.map((user) => renderItem(user))}
          </div>
        )
      }
      noExit
      closeOnOutsidePress
      closeModal={() => setIsVisible(false)}
      hideModal={!isVisible}
      modalContentWidth={PopupModalContainerWidth.SMALL}
    />
  );
};

export default ReferralHistoryModal;
